/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { axios as appAxios, handleAxiosClientError } from '@/libs/axios';
import { success, error } from '@/libs/notification';
import router from '@/router';
import {
  ShipState,
  ShipsDataState,
  ShipDetailData,
  ShipPatchNoteData,
  ShipInvoiceData,
  ShipLinkDeviceData,
  ShipCompleteData,
  ShipFilterData,
  ShipApproveData
} from '@/types/ship';
import { ElLoading } from 'element-plus';
import { handleAuthorizationError } from '@/libs/apiErrorHandler';
import { getAccessToken } from '@/libs/firebaseAuth';
import { getBaseUrl } from '@/libs/env';
import { mspfCreateDeviceErrorWhitelist } from '@/libs/mspfCreateDeviceError';

export default {
  namespaced: true,
  state: {
    listData: [],
    detailData: null,
    filterData: null
  },
  mutations: {
    setList(state: ShipState, payload: ShipsDataState[]) {
      state.listData = payload;
    },
    setDetail(state: ShipState, payload: ShipDetailData) {
      state.detailData = payload;
    },
    setFilter(state: ShipState, payload: ShipFilterData) {
      state.filterData = payload;
    }
  },
  actions: {
    async fetch(
      context: any,
      payload: { status?: string[]; isApproved?: boolean }
    ) {
      const { status, isApproved } = payload;
      const statusQuery = status
        ? `${status.map((s) => `status=${s}`).join('&')}`
        : '';

      const isApprovedQuery =
        isApproved !== undefined ? `isApproved=${isApproved}` : '';

      const queryParts = [statusQuery, isApprovedQuery].filter(Boolean);
      const queryString =
        queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

      await axios
        .get(`${getBaseUrl()}/ship${queryString}`, {
          headers: { auth: await getAccessToken() }
        })
        .then((response) => {
          const sortData = response.data.sort(
            (a: ShipsDataState, b: ShipsDataState) => {
              if (a.id > b.id) {
                return -1;
              }
              return 1;
            }
          );
          context.commit('setList', sortData);
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/top');
          } else {
            router.push('/error/server');
          }
        });
    },
    async fetchDetail(context: any, shipId: any) {
      await axios
        .get(getBaseUrl() + '/ship/' + shipId, {
          headers: { auth: await getAccessToken() }
        })
        .then((response) => {
          context.commit('setDetail', response.data);
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
          } else {
            router.push('/error/server');
          }
        });
    },
    async complete(context: any, payload: ShipCompleteData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(getBaseUrl() + '/ship/done/' + payload.id, payload.data, {
          headers: { auth: await getAccessToken() }
        })
        .then(() => {
          success('Success', '出荷完了処理に成功しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async approve(context: any, payload: ShipApproveData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/ship/approve/' + payload.id,
          { isApprove: payload.data },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success(
            'Success',
            payload.data ? '出荷を承認しました。' : '出荷を否認しました。'
          );
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async approveBulk(context: any, payload: { ids: number[] }) {
      const ids = payload.ids ?? [];
      if (ids.length === 0) return;

      const loadingInstance = ElLoading.service({ fullscreen: true });
      try {
        await axios.post(
          getBaseUrl() + '/ship/bulk/approve',
          { ids },
          { headers: { auth: await getAccessToken() } }
        );
        success('Success', '出荷を一括承認しました。');
      } catch (e: any) {
        if (e?.response?.status === 401) {
          await handleAuthorizationError();
        } else if (e?.response?.status == 404) {
          error(e.response.status, '対象の出荷情報が存在しません。');
        } else if (e?.response?.status < 500) {
          error(e.response.status);
        } else {
          router.push('/error/server');
        }
      } finally {
        loadingInstance.close();
      }
    },
    async patchNote(context: any, payload: ShipPatchNoteData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .patch(
          getBaseUrl() + '/ship/note/' + payload.id,
          { note: payload.note },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          loadingInstance.close();
          success('Success', '出荷用備考を更新しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async patchInvoice(context: any, payload: ShipInvoiceData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .patch(
          getBaseUrl() + '/ship/invoice/' + payload.id,
          {
            invoiceCode: payload.invoiceCode,
            invoiceSupplier: payload.invoiceSupplier
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          loadingInstance.close();
          success('Success', '送り状番号を更新しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async patchDeliveryNote(context: any, payload: ShipPatchNoteData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .patch(
          getBaseUrl() + '/ship/note/delivery/' + payload.id,
          { deliveryNote: payload.note },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          loadingInstance.close();
          success('Success', '納品書備考を更新しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async preparation(context: any, shipId: string) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/ship/response/' + shipId,
          {},
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          loadingInstance.close();
          success('Success', 'ステータスを 準備中 に更新しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async unpreparation(context: any, shipId: string) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/ship/unresponse/' + shipId,
          {},
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          loadingInstance.close();
          success('Success', 'ステータスを 依頼 に更新しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async linkDevice(context: any, payload: ShipLinkDeviceData) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      try {
        await appAxios.post('/ship/link/' + payload.id, {
          deviceLedgerIds: payload.devices
        });
        success('Success', '端末のリンクに成功しました。');
      } catch (e: any) {
        const status = e.response?.status;
        if (status == 404) {
          error(e.response.status, '対象の出荷情報が存在しません。');
          router.push('/ship');
        } else if (status != null && status < 500 && status !== 401) {
          await handleAxiosClientError(e, {
            whitelistPrefixes: mspfCreateDeviceErrorWhitelist
          });
          router.push('/ship');
        }
      } finally {
        loadingInstance.close();
      }
    },
    async unlinkDevice(context: any, shipId: any) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/ship/unlink/' + shipId,
          {},
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          loadingInstance.close();
          success('Success', '端末のリンク解除に成功しました。');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status == 404) {
            error(e.response.status, '対象の出荷情報が存在しません。');
            router.push('/ship');
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async remand(context: any, shipId: any) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/ship/remand/' + shipId,
          {},
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(() => {
          success('Success', '出荷情報を差し戻しました。');
          router.push('/ship');
        })
        .catch(async (e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            await handleAuthorizationError();
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/ship');
          } else {
            router.push('/error/server');
          }
        });
    },
    async setFilterData(context: any, payload: ShipFilterData) {
      context.commit('setFilter', payload);
    }
  }
};
